import React, { useEffect, useState, useMemo } from "react";
import {
    Box, CssBaseline, Breadcrumbs, Link, Typography, Avatar,
    Stack, Button, Paper, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, TextField, InputAdornment,
    CircularProgress, Dialog, DialogTitle, DialogContent,
    DialogActions, Snackbar, Alert, Chip, Divider, IconButton
} from '@mui/material';
import { Search, Visibility, OpenInNew, Close, PeopleAltOutlined, InboxOutlined } from '@mui/icons-material';
import { useNavigate } from "react-router-dom";

import Header from "../../component/header/Header";

const API_BASE = "https://www.geniusmileniaeducation.link/db-api";

// --- Design tokens (original brand colors, kept as-is) ---
const colors = {
    pageBg: '#F1F2F6',
    accent: '#FFC107',
    accentSoft: '#FFF9E6',
    cardBorder: '#F0E6CC',
    tableHeaderBg: '#FDF7E2',
    text: '#232529',
    textMuted: '#8A8F98',
};

const statusOptions = ["New", "Approved", "Rejected"];

// Colour + accent dot per status, shared by the table chip and the dialog status-picker
const statusMeta = {
    'New': { bg: '#FFF3E0', color: '#EF6C00' },
    'Approved': { bg: '#E8F5E9', color: '#2E7D32' },
    'Rejected': { bg: '#FFEBEE', color: '#C62828' },
};

const getStatusMeta = (status) => statusMeta[status] || statusMeta['New'];

const fieldSx = {
    '& .MuiOutlinedInput-root': {
        backgroundColor: '#fff',
        borderRadius: '10px',
    },
};

// Small pill chip used in the table to show a registration's current status
const StatusChip = ({ status }) => {
    const meta = getStatusMeta(status);
    return (
        <Chip
            size="small"
            label={
                <Stack direction="row" alignItems="center" spacing={0.75}>
                    <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: meta.color }} />
                    <span>{status || 'New'}</span>
                </Stack>
            }
            sx={{ bgcolor: meta.bg, color: meta.color, fontWeight: 700, borderRadius: '999px' }}
        />
    );
};

// Click-to-select status changer — a row of chips instead of a dropdown, so the four
// possible statuses (and which one is active) are all visible at a glance.
const StatusPicker = ({ value, onChange, disabled }) => (
    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        {statusOptions.map((s) => {
            const meta = getStatusMeta(s);
            const active = (value || 'New') === s;
            return (
                <Chip
                    key={s}
                    label={s}
                    onClick={() => !disabled && !active && onChange(s)}
                    disabled={disabled}
                    sx={{
                        fontWeight: 700,
                        borderRadius: '999px',
                        px: 0.5,
                        cursor: active ? 'default' : 'pointer',
                        bgcolor: active ? meta.color : meta.bg,
                        color: active ? '#fff' : meta.color,
                        border: active ? 'none' : `1px solid ${meta.color}33`,
                        '&:hover': { bgcolor: active ? meta.color : meta.bg },
                    }}
                />
            );
        })}
    </Stack>
);

const RegistrationListScreen = () => {
    const navigation = useNavigate();

    const [registrationList, setRegistrationList] = useState([]);
    const [loading, setLoading] = useState(false);

    const [filterName, setFilterName] = useState('');
    const [filterStatus, setFilterStatus] = useState('New');

    const [selected, setSelected] = useState(null);
    const [openDetail, setOpenDetail] = useState(false);
    const [updatingStatus, setUpdatingStatus] = useState(false);

    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

    useEffect(() => {
        fetchRegistrations();
        localStorage.setItem("current_tab", "Registration");
    }, []);

    const fetchRegistrations = async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API_BASE}/register`);
            const json = await res.json();
            setRegistrationList(json.data || []);
        } catch (error) {
            setSnackbar({ open: true, message: "Connection Error", severity: "error" });
        } finally {
            setLoading(false);
        }
    };

    const filteredData = useMemo(() => {
        const nameQuery = filterName.trim().toLowerCase();
        return registrationList.filter((r) => {
            const matchesName = !nameQuery ||
                r.father?.name?.toLowerCase().includes(nameQuery) ||
                r.mother?.name?.toLowerCase().includes(nameQuery) ||
                r.students?.some(s => s.name?.toLowerCase().includes(nameQuery));
            const matchesStatus = !filterStatus || r.status === filterStatus;
            return matchesName && matchesStatus;
        });
    }, [registrationList, filterName, filterStatus]);

    const statusCounts = useMemo(() => {
        const counts = { All: registrationList.length };
        statusOptions.forEach((s) => { counts[s] = 0; });
        registrationList.forEach((r) => {
            const s = r.status || 'New';
            counts[s] = (counts[s] || 0) + 1;
        });
        return counts;
    }, [registrationList]);

    const openDetailDialog = (record) => {
        setSelected(record);
        setOpenDetail(true);
    };

    const handleStatusChange = async (newStatus) => {
        if (!selected) return;
        if (selected.status === 'Approved' || selected.status === 'Rejected') return; // locked, no further changes allowed
        setUpdatingStatus(true);
        try {
            const res = await fetch(`${API_BASE}/register/${selected.registrationId}/status`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ status: newStatus })
            });
            const json = await res.json();
            if (json.status === 1) {
                setSelected({ ...selected, status: newStatus });
                setRegistrationList((prev) => prev.map((r) => r.registrationId === selected.registrationId ? { ...r, status: newStatus } : r));
                setSnackbar({ open: true, message: "Status updated", severity: "success" });
            } else {
                setSnackbar({ open: true, message: json.message || "Failed to update status", severity: "error" });
            }
        } catch (error) {
            setSnackbar({ open: true, message: "Failed to update status", severity: "error" });
        } finally {
            setUpdatingStatus(false);
        }
    };

    return (
        <Box sx={{ display: 'flex', bgcolor: colors.pageBg, minHeight: '100vh' }}>
            <CssBaseline />
            <Box component="main" sx={{ flexGrow: 1, p: 3, bgcolor: "white", m: 2, borderRadius: 5 }}>

                <Breadcrumbs sx={{ mb: 2 }}>
                    <Link underline="hover" color="inherit" onClick={() => navigation("/Portal")} sx={{ cursor: 'pointer' }}>Portal</Link>
                    <Typography color="text.primary">Registrations</Typography>
                </Breadcrumbs>

                <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={1.5} mb={3}>
                    <Box>
                        <Header title="Registration Submissions" />
                    </Box>
                    <Button
                        variant="contained"
                        onClick={fetchRegistrations}
                        disabled={loading}
                        sx={{ borderRadius: '999px', textTransform: 'none', fontWeight: 700, bgcolor: colors.accent, color: colors.text, '&:hover': { bgcolor: '#E6AC00' } }}
                    >
                        {loading ? "Refreshing..." : "Refresh List"}
                    </Button>
                </Stack>

                <Paper elevation={0} sx={{ p: 2.5, mb: 3, bgcolor: colors.accentSoft, borderRadius: 4 }}>
                    <Stack spacing={2}>
                        <TextField
                            placeholder="Filter by parent or student name..."
                            size="small"
                            fullWidth
                            value={filterName}
                            onChange={(e) => setFilterName(e.target.value)}
                            sx={{ ...fieldSx, maxWidth: { sm: 360 } }}
                            InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
                        />
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
                            <Chip
                                label={`Semua (${statusCounts.All})`}
                                onClick={() => setFilterStatus('')}
                                sx={{
                                    fontWeight: 700, borderRadius: '999px',
                                    bgcolor: !filterStatus ? colors.text : '#fff',
                                    color: !filterStatus ? '#fff' : colors.text,
                                    border: !filterStatus ? 'none' : `1px solid ${colors.cardBorder}`,
                                }}
                            />
                            {statusOptions.map((s) => {
                                const meta = getStatusMeta(s);
                                const active = filterStatus === s;
                                return (
                                    <Chip
                                        key={s}
                                        label={`${s} (${statusCounts[s] || 0})`}
                                        onClick={() => setFilterStatus(active ? '' : s)}
                                        sx={{
                                            fontWeight: 700, borderRadius: '999px',
                                            bgcolor: active ? meta.color : '#fff',
                                            color: active ? '#fff' : meta.color,
                                            border: active ? 'none' : `1px solid ${meta.color}33`,
                                        }}
                                    />
                                );
                            })}
                            {(filterName || filterStatus) && (
                                <Button size="small" onClick={() => { setFilterName(''); setFilterStatus(''); }} sx={{ textTransform: 'none', fontWeight: 600 }}>
                                    Clear filters
                                </Button>
                            )}
                        </Stack>
                    </Stack>
                </Paper>

                <TableContainer component={Paper} elevation={0} sx={{ border: `1px solid ${colors.cardBorder}`, borderRadius: 4, maxHeight: '60vh' }}>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ bgcolor: colors.tableHeaderBg, fontWeight: 700 }}>Submitted</TableCell>
                                <TableCell sx={{ bgcolor: colors.tableHeaderBg, fontWeight: 700 }}>Father Name</TableCell>
                                <TableCell sx={{ bgcolor: colors.tableHeaderBg, fontWeight: 700 }}>Mother Name</TableCell>
                                <TableCell sx={{ bgcolor: colors.tableHeaderBg, fontWeight: 700 }}>Students</TableCell>
                                <TableCell sx={{ bgcolor: colors.tableHeaderBg, fontWeight: 700 }}>Status</TableCell>
                                <TableCell align="center" sx={{ bgcolor: colors.tableHeaderBg, fontWeight: 700 }}>Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow><TableCell colSpan={6} align="center" sx={{ py: 6 }}><CircularProgress size={30} sx={{ color: colors.accent }} /></TableCell></TableRow>
                            ) : filteredData.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                                        <Stack alignItems="center" spacing={1}>
                                            <InboxOutlined sx={{ fontSize: 36, color: colors.textMuted }} />
                                            <Typography color="text.secondary">No registrations found.</Typography>
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            ) : filteredData.map((record) => (
                                <TableRow key={record.registrationId} hover sx={{ '&:last-child td': { border: 0 } }}>
                                    <TableCell>
                                        <Typography variant="body2" color="text.secondary">{record.createdAtDisplay || '-'}</Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Stack direction="row" spacing={1.5} alignItems="center">
                                            <Avatar sx={{ bgcolor: colors.accentSoft, color: colors.accent, fontWeight: 700, fontSize: 14 }}>
                                                {record.father?.name?.charAt(0)?.toUpperCase() || '?'}
                                            </Avatar>
                                            <Typography variant="subtitle2" fontWeight={600}>{record.father?.name || '-'}</Typography>
                                        </Stack>
                                    </TableCell>
                                    <TableCell>{record.mother?.name || '-'}</TableCell>
                                    <TableCell>
                                        <Stack direction="row" spacing={0.5} alignItems="center">
                                            <PeopleAltOutlined sx={{ fontSize: 16, color: colors.textMuted }} />
                                            <Typography variant="body2">{record.students?.length || 0}</Typography>
                                        </Stack>
                                    </TableCell>
                                    <TableCell>
                                        <StatusChip status={record.status} />
                                    </TableCell>
                                    <TableCell align="center">
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            startIcon={<Visibility />}
                                            onClick={() => openDetailDialog(record)}
                                            sx={{ borderRadius: '999px', textTransform: 'none', fontWeight: 600, borderColor: colors.cardBorder, color: colors.text }}
                                        >
                                            View
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* Detail Dialog */}
                <Dialog fullWidth maxWidth="sm" open={openDetail} onClose={() => setOpenDetail(false)} PaperProps={{ sx: { borderRadius: 5 } }}>
                    <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        Registration Detail
                        <IconButton size="small" onClick={() => setOpenDetail(false)}>
                            <Close fontSize="small" />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent dividers>
                        {selected && (
                            <Stack spacing={2.5}>
                                <Box>
                                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: colors.text, mb: 1 }}>
                                        Status Pendaftaran
                                    </Typography>
                                    {(selected.status === 'Approved' || selected.status === 'Rejected') ? (
                                        <Stack direction="row" alignItems="center" spacing={1.5}>
                                            <StatusChip status={selected.status} />
                                        </Stack>
                                    ) : (
                                        <Stack direction="row" alignItems="center" spacing={1.5}>
                                            <StatusPicker
                                                value={selected.status}
                                                onChange={handleStatusChange}
                                                disabled={updatingStatus}
                                            />
                                            {updatingStatus && <CircularProgress size={18} sx={{ color: colors.accent }} />}
                                        </Stack>
                                    )}
                                </Box>

                                <Divider sx={{ borderColor: colors.cardBorder }} />

                                <Box>
                                    <Typography variant="subtitle2" fontWeight={700}>AYAH/PENJAGA</Typography>
                                    <Typography variant="body2">{selected.father?.name || '-'} — {selected.father?.icNumber || '-'}</Typography>
                                    <Typography variant="body2" color="text.secondary">{selected.father?.occupation || '-'} · {selected.father?.phone || '-'}</Typography>
                                </Box>

                                <Box>
                                    <Typography variant="subtitle2" fontWeight={700}>IBU/PENJAGA</Typography>
                                    <Typography variant="body2">{selected.mother?.name || '-'} — {selected.mother?.icNumber || '-'}</Typography>
                                    <Typography variant="body2" color="text.secondary">{selected.mother?.occupation || '-'} · {selected.mother?.phone || '-'}</Typography>
                                </Box>

                                <Divider sx={{ borderColor: colors.cardBorder }} />
                                <Typography variant="subtitle2" fontWeight={700}>PELAJAR ({selected.students?.length || 0})</Typography>
                                {selected.students?.map((s, i) => (
                                    <Paper key={i} elevation={0} sx={{ p: 2, borderRadius: 3, border: `1px solid ${colors.cardBorder}` }}>
                                        <Typography variant="body2" fontWeight={700}>{s.name} ({s.age} tahun)</Typography>
                                        <Typography variant="body2" color="text.secondary">IC: {s.icNumber} · Tel: {s.phone || '-'}</Typography>
                                        <Typography variant="body2" color="text.secondary">{s.address}</Typography>
                                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                                            Kesihatan: {s.isHealthy === 'no' ? `⚠ ${s.healthDetails}` : 'Sihat'}
                                        </Typography>
                                        {s.orphanCertUrls?.length > 0 && (
                                            <Stack direction="row" spacing={1} sx={{ mt: 0.5 }} flexWrap="wrap">
                                                {s.orphanCertUrls.map((url, idx) => (
                                                    <Link key={idx} href={url} target="_blank" rel="noopener" underline="hover" variant="caption">
                                                        Sijil {idx + 1} <OpenInNew sx={{ fontSize: 12 }} />
                                                    </Link>
                                                ))}
                                            </Stack>
                                        )}
                                        <Typography variant="body2" sx={{ mt: 0.5 }}>Pakej: {s.package}</Typography>
                                        {s.electiveSubjects && <Typography variant="body2">Elektif: {s.electiveSubjects}</Typography>}
                                        <Typography variant="body2">Hari Kelas: {s.classDays?.join(', ')}</Typography>
                                    </Paper>
                                ))}

                                <Divider sx={{ borderColor: colors.cardBorder }} />
                                <Stack direction="row" alignItems="center" justifyContent="space-between">
                                    <Typography variant="subtitle2" fontWeight={700}>Bukti Pembayaran</Typography>
                                    {selected.paymentProofUrl ? (
                                        <Link href={selected.paymentProofUrl} target="_blank" rel="noopener" underline="hover">
                                            View File <OpenInNew sx={{ fontSize: 14 }} />
                                        </Link>
                                    ) : (
                                        <Typography variant="body2" color="text.secondary">None</Typography>
                                    )}
                                </Stack>
                            </Stack>
                        )}
                    </DialogContent>
                    <DialogActions sx={{ p: 2 }}>
                        <Button onClick={() => setOpenDetail(false)} sx={{ borderRadius: '999px', textTransform: 'none', fontWeight: 600 }}>
                            Close
                        </Button>
                    </DialogActions>
                </Dialog>

                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={4000}
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                >
                    <Alert severity={snackbar.severity} variant="filled" sx={{ borderRadius: 2 }}>
                        {snackbar.message}
                    </Alert>
                </Snackbar>
            </Box>
        </Box>
    );
};

export default RegistrationListScreen;